/**
 * Hook para gestionar el proceso manual completo de HomeCenter
 * Orquesta la consulta de pedidos, cálculo de fechas y sincronización por fecha y tipoOrden
 */
import { useState, useCallback } from 'react'
import { HomeCenterManualService } from '../../services/cron/homeCenterManualService'
import type {
  HomeCenterManualProcessPhase,
  HomeCenterManualProcessProgress,
  HomeCenterManualProcessResults,
  SyncByDateDetail,
  SyncByDateResponse,
} from '../../types/cron/homecenterManual'
import { appendHomeCenterDetalles } from '../../utils/homeCenterSyncUtils'

interface UseHomeCenterManualProcessReturn {
  isProcessing: boolean
  currentPhase: HomeCenterManualProcessPhase['phase']
  progress: HomeCenterManualProcessProgress
  results: HomeCenterManualProcessResults
  error: string | null
  execute: () => Promise<void>
  reset: () => void
}

const INITIAL_PROGRESS: HomeCenterManualProcessProgress = {
  ordersFetched: 0,
  totalOrders: 0,
  currentPage: 0,
  totalPages: 0,
  oldestDate: null,
  startDate: null,
  currentTipoOrden: null,
  currentDate: null,
  totalDates: 0,
  processedDates: 0,
  totalTipoOrdenes: 3,
  processedTipoOrdenes: 0,
}

const INITIAL_RESULTS: HomeCenterManualProcessResults = {
  byTipoOrden: {},
  summary: {
    totalDates: 0,
    processedDates: 0,
    totalCreated: 0,
    totalSkipped: 0,
    totalErrors: 0,
    totalDetalles: 0,
  },
}

/**
 * Consolidar resultados del procesamiento
 */
function consolidateResults(
  allResults: Array<{ tipoOrden: number; results: SyncByDateResponse[] }>
): HomeCenterManualProcessResults {
  const byTipoOrden: HomeCenterManualProcessResults['byTipoOrden'] = {}
  let totalDates = 0
  let processedDates = 0
  let totalCreated = 0
  let totalSkipped = 0
  let totalErrors = 0
  let totalDetalles = 0

  for (const { tipoOrden, results } of allResults) {
    let tipoOrdenCreated = 0
    let tipoOrdenSkipped = 0
    let tipoOrdenErrors = 0
    const tipoOrdenDetalles: SyncByDateDetail[] = []

    for (const result of results) {
      if (result.success) {
        const r = result.results
        if (r) {
          tipoOrdenCreated += r.created ?? 0
          tipoOrdenSkipped += r.skipped ?? 0
          tipoOrdenErrors += r.errors ?? 0
          appendHomeCenterDetalles(tipoOrdenDetalles, r)
        }
      } else {
        tipoOrdenErrors += 1
      }
    }

    byTipoOrden[tipoOrden] = {
      totalDates: results.length,
      processedDates: results.length,
      totalCreated: tipoOrdenCreated,
      totalSkipped: tipoOrdenSkipped,
      totalErrors: tipoOrdenErrors,
      detalles: tipoOrdenDetalles,
    }

    totalDates += results.length
    processedDates += results.length
    totalCreated += tipoOrdenCreated
    totalSkipped += tipoOrdenSkipped
    totalErrors += tipoOrdenErrors
    totalDetalles += tipoOrdenDetalles.length
  }

  return {
    byTipoOrden,
    summary: {
      totalDates,
      processedDates,
      totalCreated,
      totalSkipped,
      totalErrors,
      totalDetalles,
    },
  }
}

export function useHomeCenterManualProcess(): UseHomeCenterManualProcessReturn {
  const [isProcessing, setIsProcessing] = useState(false)
  const [currentPhase, setCurrentPhase] =
    useState<HomeCenterManualProcessPhase['phase']>('idle')
  const [progress, setProgress] =
    useState<HomeCenterManualProcessProgress>(INITIAL_PROGRESS)
  const [results, setResults] =
    useState<HomeCenterManualProcessResults>(INITIAL_RESULTS)
  const [error, setError] = useState<string | null>(null)

  /**
   * Ejecutar el proceso completo
   */
  const execute = useCallback(async () => {
    setIsProcessing(true)
    setError(null)
    setCurrentPhase('fetching_orders')
    setProgress(INITIAL_PROGRESS)
    setResults(INITIAL_RESULTS)

    try {
      // FASE 1: Consultar pedidos y calcular fecha inicial
      const fetchedOrders = await HomeCenterManualService.getAllHomeCenterOrders(
        (fetchProgress) => {
          setProgress((prev) => ({
            ...prev,
            currentPage: fetchProgress.currentPage,
            totalPages: fetchProgress.totalPages,
            ordersFetched: fetchProgress.ordersFetched,
            totalOrders: fetchProgress.ordersFetched, // Aproximación
          }))
        }
      )

      setProgress((prev) => ({
        ...prev,
        totalOrders: fetchedOrders.length,
      }))

      // Validar que haya pedidos para procesar
      if (fetchedOrders.length === 0) {
        setCurrentPhase('completed')
        setResults(INITIAL_RESULTS)
        setIsProcessing(false)
        return
      }

      // Calcular fecha inicial
      const startDate = HomeCenterManualService.calculateStartDate(fetchedOrders)
      const oldestDate = new Date(
        Math.min(...fetchedOrders.map((o) => new Date(o.date_created).getTime()))
      )

      // Generar rango de fechas
      const dates = HomeCenterManualService.generateDateRange(startDate)

      setProgress((prev) => ({
        ...prev,
        oldestDate: oldestDate.toISOString().split('T')[0],
        startDate: startDate.toISOString().split('T')[0],
        totalDates: dates.length,
      }))

      // FASE 2: Procesar por tipoOrden (1, 4, 8) fecha por fecha
      setCurrentPhase('processing_dates')
      const tipoOrdenes = HomeCenterManualService.getTipoOrdenes()
      const allResults: Array<{
        tipoOrden: number
        results: SyncByDateResponse[]
      }> = []

      for (let i = 0; i < tipoOrdenes.length; i++) {
        const tipoOrden = tipoOrdenes[i]

        setProgress((prev) => ({
          ...prev,
          currentTipoOrden: tipoOrden,
          processedTipoOrdenes: i,
        }))

        const tipoOrdenResults = await HomeCenterManualService.processAllDates(
          dates,
          tipoOrden,
          (dateProgress) => {
            setProgress((prev) => ({
              ...prev,
              currentDate: dateProgress.currentDate,
              processedDates: dateProgress.processedDates,
            }))
          }
        )

        allResults.push({
          tipoOrden,
          results: tipoOrdenResults,
        })

        setProgress((prev) => ({
          ...prev,
          processedTipoOrdenes: i + 1,
        }))
      }

      // FASE 3: Consolidar resultados
      let consolidatedResults: HomeCenterManualProcessResults
      try {
        consolidatedResults = consolidateResults(allResults)
      } catch (consolidateErr: unknown) {
        const msg =
          consolidateErr instanceof Error
            ? consolidateErr.message
            : String(consolidateErr)
        throw new Error(
          `Error al consolidar resultados del proceso manual: ${msg}`
        )
      }

      setResults(consolidatedResults)
      setCurrentPhase('completed')
    } catch (err: unknown) {
      console.error('Error en proceso manual de HomeCenter:', err)
      const message =
        err instanceof Error
          ? err.message
          : 'Error desconocido durante el proceso manual de HomeCenter'
      setError(message)
      setCurrentPhase('error')
    } finally {
      setIsProcessing(false)
    }
  }, [])

  /**
   * Resetear estado
   */
  const reset = useCallback(() => {
    setIsProcessing(false)
    setCurrentPhase('idle')
    setProgress(INITIAL_PROGRESS)
    setResults(INITIAL_RESULTS)
    setError(null)
  }, [])

  return {
    isProcessing,
    currentPhase,
    progress,
    results,
    error,
    execute,
    reset,
  }
}

